import React, {useCallback, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {RESOURCES as resources} from '../productResources';

/**
 * Builds the request URL for a product's thumbnail.
 *
 * Ported from web-admin (!191 and its review follow-ups) for web-portal#10.
 *
 * The API writes every upload for a product to the same object name, and the
 * production ingress lets browsers cache thumbnails for 30 days, so a replaced
 * image keeps its URL and the browser keeps showing the old copy. The product's
 * modified time goes on the URL as a version: the API bumps it on every image
 * upload, so a replaced image gets a fresh URL while unchanged products keep
 * theirs and stay cached. The ingress and the API route on the path alone, so
 * the query is carried through and ignored server-side.
 *
 * @param {string} path the stored image path, e.g. /assets/org-1/show-2/product-3.jpg
 * @param {string|number} [modified] the product's modified time
 * @return {string} the thumbnail URL, versioned when a usable modified time is given
 *                  (a missing, unparseable or zero time leaves it unversioned);
 *                  an empty string without a path
 */
export const thumbnailUrl = (path, modified) => {
    if (!path) {
        return '';
    }
    const thumbPath = path.replace('/assets', '/thumbnail');
    const {uri} = resources.ProductImageThumbnail.GET_ONE({image_path: thumbPath});
    const version = (typeof modified === 'number') ? modified : Date.parse(modified);
    // Go's zero time on never-modified rows parses to a negative epoch: no version.
    return (version > 0) ? uri + '?v=' + version : uri;
};

/**
 * A component to retrieve and display a Product Thumbnail for a list
 *
 * @param bypassCache ask the browser to go to the server for this request
 *        instead of serving a cached copy. Only for a row known to be
 *        incomplete: right after a save, react-admin renders the record before
 *        it carries a modified time, so the request cannot be versioned.
 * @param id the product (or item) id
 * @param modified the product's modified time; versions the request so a
 *        replaced image is fetched again. Leave it out when the row carries no
 *        product timestamp: the request is then unversioned and cacheable.
 * @param title the alt text
 * @param thumbnail the stored image path
 */
const ProductThumbnail = ({bypassCache = false, id = '', modified, title = '', thumbnail = ''}) => {

    const mounted = useRef(false);
    const latestRequest = useRef(0);
    const objectUrl = useRef('');
    const [productId, setProductId] = useState(id);
    const [productThumbnail, setProductThumbnail] = useState('');
    const [productModified, setProductModified] = useState('');
    const [src, setSrc] = useState('');

    // Shows an image (or none, with ''), releasing the blob URL it replaces.
    const showImage = useCallback((url) => {
        if (objectUrl.current && objectUrl.current !== url) {
            URL.revokeObjectURL(objectUrl.current);
        }
        objectUrl.current = url;
        setSrc(url);
    }, []);

    const fetchData = useCallback((path, version, noCache) => {
        const request = ++latestRequest.current;
        if (path && path !== '') {
            const imageType = path.substring(path.indexOf('.') + 1);
            const headers = {'Content-Type': 'image/' + imageType};
            if (noCache) {
                headers['Cache-Control'] = 'no-cache';
            }
            axios({
                method: 'GET',
                url: thumbnailUrl(path, version),
                headers: headers,
                responseType: 'blob'
            }).then((response) => {
                if (response && response.data) {
                    const obj = URL.createObjectURL(response.data);
                    // Only the newest request may set the image: an earlier one
                    // finishing last would put the old picture back.
                    if (mounted.current === true && request === latestRequest.current) {
                        showImage(obj);
                    } else {
                        URL.revokeObjectURL(obj);
                    }
                }
            }).catch((error) => {
                if (error.response) {
                    // Server response outside 2xx
                } else if (error.request) {
                    // No response
                }
            });
        }
    }, [showImage]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            if (objectUrl.current) {
                URL.revokeObjectURL(objectUrl.current);
                objectUrl.current = '';
            }
        };
    }, []);

    useEffect(() => {
        // A replaced image keeps its path, so the modified time is what
        // signals that the thumbnail has to be fetched again.
        if (id !== productId || thumbnail !== productThumbnail || modified !== productModified) {
            setProductId(id);
            setProductThumbnail(thumbnail);
            setProductModified(modified);
            showImage('');
            fetchData(thumbnail, modified, bypassCache);
        }
    }, [id, productId, thumbnail, productThumbnail, modified, productModified, bypassCache, fetchData, showImage]);

    return (
        <span className="product-image list-thumbnail">
            {src !== '' &&
                <>
                    <img src={src} alt={title}/>
                    <span className="enlarge">
                        <img src={src} alt={title}/>
                    </span>
                </>
            }
        </span>
    );
};

export default ProductThumbnail;
